import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Pencil } from "lucide-react";
import { AppButton, AppCard, AppCardContent, EmptyState, ProgressBar, StatusBadge } from "../../ui";
import { formatCurrency } from "../../../utils/format";
import RecordCardHead from "./RecordCardHead";
import { batchDates, batchSchedule } from "./studentRecord";

/** A course, its batch and timetable, how far the batch has got, and what it costs. */
const EnrollmentRow = ({ enrollment: e, progress, showFees }) => {
  const schedule = batchSchedule(e.batch);
  const dates = batchDates(e.batch);

  return (
    <li className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-5 tw-py-4 first:tw-border-t-0 md:tw-flex-row md:tw-items-start">
      <span
        className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary-soft tw-text-primary"
        aria-hidden="true"
      >
        <GraduationCap size={18} />
      </span>

      <div className="tw-min-w-0 tw-flex-1">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-semibold tw-text-content">{e.courseName}</span>
          {e.isPrimary && <StatusBadge status="outline">Primary</StatusBadge>}
          {e.status !== "active" && <StatusBadge status={e.status} domain="enrollment" />}
          {e.batch?.isLive && <StatusBadge status="live" domain="batch" />}
        </div>

        <p className="tw-m-0 tw-mt-1 tw-text-[0.8125rem] tw-text-content-muted">
          {e.batch?._id ? (
            <Link to={`/batches/${e.batch._id}`} className="tw-rounded-sm tw-font-medium tw-text-primary tw-no-underline tw-focus-ring hover:tw-underline">
              {e.batch.name || "Batch"}
            </Link>
          ) : (
            "Not assigned to a batch"
          )}
          {schedule && ` · ${schedule}`}
        </p>
        {(dates || e.batch?.status) && (
          <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-subtle">
            {dates}
            {dates && e.batch?.status && " · "}
            {e.batch?.status && `Batch ${String(e.batch.status).toLowerCase()}`}
          </p>
        )}

        {progress && (
          <div className="tw-mt-3 tw-flex tw-max-w-md tw-items-center tw-gap-3">
            <ProgressBar value={progress.percent} color="primary" size="md" />
            <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-content-muted">
              {progress.percent}% · {progress.completedSessions} of {progress.totalSessions} sessions
            </span>
          </div>
        )}
      </div>

      {showFees && e.totalFees > 0 && (
        <dl className="tw-m-0 tw-shrink-0 tw-text-[0.8125rem] md:tw-text-right">
          <dt className="tw-sr-only">Fees paid</dt>
          <dd className="tw-m-0 tw-font-semibold tw-tabular-nums tw-text-content">
            {formatCurrency(e.paidFees)}
            <span className="tw-font-normal tw-text-content-subtle"> of {formatCurrency(e.totalFees)}</span>
          </dd>
          <dt className="tw-sr-only">Balance</dt>
          <dd className={`tw-m-0 tw-text-xs tw-tabular-nums ${e.pendingFees > 0 ? "tw-text-warning-fg" : "tw-text-success-fg"}`}>
            {e.pendingFees > 0 ? `${formatCurrency(e.pendingFees)} due` : "Fully paid"}
          </dd>
        </dl>
      )}
    </li>
  );
};

/**
 * EnrollmentsCard — every course the student is enrolled in.
 *
 * Batch progress is known only for the PRIMARY batch: `/dashboard/student/:id`
 * computes syllabus completion for `Student.batch` and nothing else, so the
 * other rows show their timetable and fees but no bar. A bar at 0 % would read
 * as "no progress", which is a different claim from "not measured".
 *
 * Props:
 *   enrollments — studentRecord.listEnrollments()
 *   progress    — `courseCompletion` from the dashboard stats, for the primary
 *   showFees    — fee management is on and the viewer may see fees
 *   onEdit      — omitted when the viewer may not edit enrolments
 */
const EnrollmentsCard = ({ enrollments, progress, showFees, onEdit }) => (
  <AppCard>
    <RecordCardHead
      title="Courses"
      description={enrollments.length === 0
        ? "Not enrolled in a course"
        : `${enrollments.length} enrolment${enrollments.length > 1 ? "s" : ""}`}
      action={onEdit && (
        <AppButton size="sm" variant="outline" onClick={onEdit}>
          <Pencil size={16} /> Manage
        </AppButton>
      )}
    />
    <AppCardContent className="tw-p-0">
      {enrollments.length === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title="No course yet"
          description="Assign a course and batch from Edit to start tracking attendance and progress."
        />
      ) : (
        <ul className="tw-m-0 tw-list-none tw-p-0">
          {enrollments.map((e) => (
            <EnrollmentRow
              key={e.key}
              enrollment={e}
              showFees={showFees}
              progress={e.isPrimary && progress?.totalSessions ? progress : null}
            />
          ))}
        </ul>
      )}
    </AppCardContent>
  </AppCard>
);

export default EnrollmentsCard;
